import {
	ALERT_TYPE_LABELS,
	alertTypeLabel,
	cardReaderLabel,
	DISPLAY_FAULT,
	displayFault,
	displayLabel,
	failedSubsystems,
	isCheckoutAppBackgroundedAlert,
	isPartialCameraFailure,
	labelledFailedParts,
	NON_PERISHABLE_AUTO_CLEAR_LABEL,
	NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY,
	NON_PERISHABLE_AUTO_CLEAR_PENDING_LABEL,
	POS_APP_LABEL,
	singleFailedPartLabel,
	smartLockLabel,
	TEMPERATURE_ALARM_LABEL,
	visionLabel,
	visionQaFailed,
} from '@micromart/utils/lib/alert-labels'
import type { DisplayCheck, SmartLockCheck as SharedSmartLockCheck } from '@micromart/utils/lib/alert-labels'

import type {
	AlertActor,
	CabinetAlertEvent,
	CabinetServiceHistoryItem,
	CabinetServiceStatus,
	CabinetServiceStatusRow,
} from '@/types/cabinet-health'
import { dayjs } from '@/lib/dayjs'

export { alertTypeLabel, cardReaderLabel }

/** Alert types emitted by the monitor; compare `alert.type` against these instead of string literals. */
export const CABINET_ALERT_TYPE = {
	CabinetOffline: 'cabinetOffline',
	CardReader: 'cardReader',
	Computer: 'computer',
	DeviceNotInKioskMode: 'deviceNotInKioskMode',
	Display: 'display',
	DoorOpen: 'doorOpen',
	DoorUnlockFailed: 'doorUnlockFailed',
	EmptySlots: 'emptySlots',
	EtagHub: 'etagHub',
	FailedSystemCheck: 'failedSystemCheck',
	KioskError: 'kioskError',
	LowStock: 'lowStock',
	MaintenanceMode: 'maintenanceMode',
	NoSales: 'noSales',
	OfflineDevice: 'offlineDevice',
	PaymentTerminal: 'paymentTerminal',
	PosApp: 'posApp',
	Router: 'router',
	RouterUptimeLow: 'routerUptimeLow',
	SalesAnomaly: 'salesAnomaly',
	Shelves: 'shelves',
	SmartLock: 'smartLock',
	Temperature: 'temperature',
	TowerOffline: 'towerOffline',
	UnstableInternet: 'unstableInternet',
	Vision: 'vision',
} as const satisfies Record<string, string>
export type CabinetAlertType = (typeof CABINET_ALERT_TYPE)[keyof typeof CABINET_ALERT_TYPE]

/** Mirrors `BLOCKING_ALERT_TYPES` in super: alerts that put a cabinet out of service. */
export const BLOCKING_ALERT_TYPES = [
	CABINET_ALERT_TYPE.CabinetOffline,
	CABINET_ALERT_TYPE.FailedSystemCheck,
	CABINET_ALERT_TYPE.MaintenanceMode,
] as const

/** Cabinet service status values; compare against these instead of string literals. */
export const CABINET_SERVICE_STATUS = {
	Operational: 'operational',
	OutOfService: 'out_of_service',
	Inactive: 'inactive',
} as const satisfies Record<string, CabinetServiceStatus>

export const SERVICE_STATUS_META: Record<
	CabinetServiceStatus,
	{ label: string; color: string; channel: string; chipColor: 'success' | 'error' | 'warning' | 'default' }
> = {
	[CABINET_SERVICE_STATUS.Operational]: {
		label: 'Operational',
		color: 'var(--mui-palette-success-main)',
		channel: 'var(--mui-palette-success-mainChannel)',
		chipColor: 'success',
	},
	[CABINET_SERVICE_STATUS.OutOfService]: {
		label: 'Out of service',
		color: 'var(--mui-palette-error-main)',
		channel: 'var(--mui-palette-error-mainChannel)',
		chipColor: 'error',
	},
	[CABINET_SERVICE_STATUS.Inactive]: {
		label: 'Inactive',
		color: 'var(--mui-palette-neutral-400)',
		channel: 'var(--mui-palette-neutral-400Channel)',
		chipColor: 'default',
	},
}

export function isRestartGrace(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): boolean {
	return (
		alert.type === CABINET_ALERT_TYPE.CabinetOffline &&
		!!alert.data?.scheduled_restart &&
		!alert.data.scheduled_restart.grace_ended_at
	)
}

export function isBlockingAlert(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): boolean {
	return (
		(BLOCKING_ALERT_TYPES as readonly string[]).includes(alert.type) &&
		!isRestartGrace(alert) &&
		// Checkout app merely out of the foreground warns — the terminal still answers (QA-312).
		!isCheckoutAppBackgroundedAlert(alert as { type?: string; data?: Record<string, unknown> | null }) &&
		// A sole partial camera failure still transacts — warning, not out of service (QA-310).
		!isPartialCameraFailure(alert.data)
	)
}

/** The monitor raises `noSales` once a cabinet has gone 24h without an order, so the alert age adds to that. */
const NO_SALES_ALERT_THRESHOLD_HOURS = 24

/** "5 min" under an hour, "27h" up to 48h, then "3 days" (PRO-219). Shared by every alert age shown on the health pages. */
export function formatAlertAge(minutes: number): string {
	const clamped = Math.max(0, Math.floor(minutes))
	if (clamped < 60) return `${clamped} min`
	const hours = Math.floor(clamped / 60)
	if (hours <= 48) return `${hours}h`
	return `${Math.floor(hours / 24)} days`
}

/** Exact timestamp in the viewer's (browser) timezone, for tooltips behind relative ages. */
export function formatExactTime(input: string | Date): string {
	return dayjs(input).format('MMM D, YYYY [at] h:mm A')
}

/** "Wednesday, September 19, 2026 at 2:14 PM" — long form for resolved-time tooltips. */
export function formatFullTimestamp(input: string | Date): string {
	return dayjs(input).format('dddd, MMMM D, YYYY [at] h:mm A')
}

/**
 * Compact resolved time: "2 hours ago" today, "Yesterday, 10:36 PM", or "Sep 19, 2:14 PM".
 * Full timestamp belongs in a tooltip via `formatFullTimestamp`.
 */
export function formatResolvedWhen(input: string | Date, now = dayjs()): string {
	const date = dayjs(input)
	if (!date.isValid()) return '—'
	if (date.isSame(now, 'day')) return date.from(now)
	const time = date.format('h:mm A')
	if (date.isSame(now.subtract(1, 'day'), 'day')) return `Yesterday, ${time}`
	if (!date.isSame(now, 'year')) return date.format('MMM D, YYYY [at] h:mm A')
	return `${date.format('MMM D')}, ${time}`
}

/** Age of an alert (until it resolved, or now), formatted with `formatAlertAge`. */
export function alertAge(
	alert: Pick<CabinetAlertEvent, 'detected_at' | 'created_at' | 'resolved_at'>,
	now = dayjs()
): string {
	const end = alert.resolved_at ? dayjs(alert.resolved_at) : now
	return formatAlertAge(end.diff(dayjs(alertStartedAt(alert)), 'minute'))
}

/**
 * "No sales in 27h" up to 48h, then "No sales in 3 days". Measured from the cabinet's last sale
 * when known (open alerts only); otherwise estimated as the alert threshold plus the alert age.
 * A cabinet that has never sold anything (`site.last_customer_open_at === null`) reads "No sales recorded".
 */
export function noSalesLabel(
	alert: Pick<CabinetAlertEvent, 'detected_at' | 'created_at' | 'resolved_at'>,
	lastSaleAt?: string | null,
	now = dayjs()
): string {
	if (lastSaleAt === null && !alert.resolved_at) return 'No sales recorded'
	if (alert.resolved_at) {
		const minutes =
			NO_SALES_ALERT_THRESHOLD_HOURS * 60 +
			Math.max(0, dayjs(alert.resolved_at).diff(dayjs(alertStartedAt(alert)), 'minute'))
		return `No sales in ${formatAlertAge(minutes)}`
	}
	const since = lastSaleAt
		? dayjs(lastSaleAt)
		: dayjs(alertStartedAt(alert)).subtract(NO_SALES_ALERT_THRESHOLD_HOURS, 'hour')
	return `No sales since ${formatShortDate(since, now)}`
}

export const SALES_ANOMALY_TRIGGER = { Drop: 'drop', Quiet: 'quiet' } as const

interface SalesAnomalyData {
	trigger?: (typeof SALES_ANOMALY_TRIGGER)[keyof typeof SALES_ANOMALY_TRIGGER]
	expected_sales?: number
	actual_sales?: number
	window_hours?: number
}

function salesAnomalyFigures(
	alert: Pick<CabinetAlertEvent, 'data'>
): { window: string; usual: number; actual: number; quiet: boolean } | null {
	const data = (alert.data ?? {}) as SalesAnomalyData
	if (typeof data.expected_sales !== 'number' || typeof data.actual_sales !== 'number' || !data.window_hours)
		return null
	return {
		window: formatAlertAge(data.window_hours * 60),
		usual: Math.round(data.expected_sales),
		actual: data.actual_sales,
		quiet: data.trigger === SALES_ANOMALY_TRIGGER.Quiet || data.actual_sales === 0,
	}
}

/** "No sales in 27h, usually ~12" or "7 sold in 3 days, usually ~35" (QA-277). */
export function salesAnomalyDetail(alert: Pick<CabinetAlertEvent, 'data'>): string | null {
	const figures = salesAnomalyFigures(alert)
	if (!figures) return null
	if (figures.quiet) return `No sales in ${figures.window}, usually ~${figures.usual}`
	return `${figures.actual} sold in ${figures.window}, usually ~${figures.usual}`
}

/** "Sales in the past 3 days: 9 sold vs 20 typically", shown in the recommended-action box. */
export function salesAnomalyActionDetail(alert: Pick<CabinetAlertEvent, 'data'>): string | null {
	const figures = salesAnomalyFigures(alert)
	if (!figures) return null
	const sold = figures.quiet ? 'no sales' : `${figures.actual} sold`
	return `Sales in the past ${figures.window}: ${sold} vs ${figures.usual} typically`
}

interface StockAlertData {
	stocked_percent?: number
	empty_slots?: number
	total_slots?: number
}

/** "15 of 35 slots empty" for Low stock, "9 of 38 slots out of stock" for Out of stock; null when no slots are empty. */
export function stockSlotsDetail(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): string | null {
	const data = (alert.data ?? {}) as StockAlertData
	if (typeof data.empty_slots !== 'number' || typeof data.total_slots !== 'number' || data.empty_slots <= 0) return null
	if (alert.type === CABINET_ALERT_TYPE.EmptySlots) return `${data.empty_slots} of ${data.total_slots} slots out of stock`
	if (alert.type === CABINET_ALERT_TYPE.LowStock) return `${data.empty_slots} of ${data.total_slots} slots empty`
	return null
}

/** "25% stocked • 15 of 35 slots empty" for Low stock, "9 of 38 slots out of stock" for Out of stock (QA-317). */
export function stockAlertDetail(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): string | null {
	const data = (alert.data ?? {}) as StockAlertData
	if (typeof data.empty_slots !== 'number' || typeof data.total_slots !== 'number') return null
	if (alert.type === CABINET_ALERT_TYPE.EmptySlots) return `${data.empty_slots} of ${data.total_slots} slots out of stock`
	if (typeof data.stocked_percent !== 'number') return null
	const slots = stockSlotsDetail(alert)
	return slots ? `${data.stocked_percent}% stocked • ${slots}` : `${data.stocked_percent}% stocked`
}

/** "Low stock (25% stocked)" or "Out of stock (9 slots empty)". */
function stockAlertTitle(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): string {
	const label = alertTypeLabel(alert.type)
	const data = (alert.data ?? {}) as StockAlertData
	if (alert.type === CABINET_ALERT_TYPE.LowStock && typeof data.stocked_percent === 'number') {
		return `${label} (${data.stocked_percent}% stocked)`
	}
	if (alert.type === CABINET_ALERT_TYPE.EmptySlots && typeof data.empty_slots === 'number') {
		const slots = data.empty_slots === 1 ? 'slot' : 'slots'
		return `${label} (${data.empty_slots} ${slots} empty)`
	}
	return label
}

/** Supporting numbers shown under an issue's title, when the alert has them. */
export function alertSubDetail(alert: CabinetAlertEvent): string | null {
	if (alert.type === CABINET_ALERT_TYPE.SalesAnomaly) return salesAnomalyDetail(alert)
	if (alert.type === CABINET_ALERT_TYPE.LowStock || alert.type === CABINET_ALERT_TYPE.EmptySlots) {
		return stockAlertDetail(alert)
	}
	return null
}

/** "Oct 3", or "Oct 3, 2025" outside the current year. */
export function formatShortDate(input: string | Date | dayjs.Dayjs, now = dayjs()): string {
	const date = dayjs(input)
	return date.format(date.year() === now.year() ? 'MMM D' : 'MMM D, YYYY')
}

/** "Inactive • last seen Aug 5" for a cabinet whose devices have been silent for 30+ days. */
export function inactiveLabel(row: Pick<CabinetServiceStatusRow, 'last_seen_at'>, now = dayjs()): string {
	return row.last_seen_at ? `Inactive • last seen ${formatShortDate(row.last_seen_at, now)}` : 'Inactive • never seen'
}

interface SmartLockCheck {
	status?: { data?: { clientStatus?: string } }
}

/**
 * The cooler controller reports a latched high temperature alarm through the smart lock check
 * (`clientStatus: 'alarm'`); it appears on the composite `failedSystemCheck` (full check
 * response) and on the per-subsystem `smartLock` alert (that subsystem's status).
 */
export function isTemperatureAlarm(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): boolean {
	const data = alert.data ?? {}
	let smartLock: SmartLockCheck | undefined
	if (alert.type === CABINET_ALERT_TYPE.FailedSystemCheck) {
		smartLock = (data as { smartLock?: SmartLockCheck }).smartLock
	} else if (alert.type === CABINET_ALERT_TYPE.SmartLock) {
		smartLock = data as SmartLockCheck
	}
	return smartLock?.status?.data?.clientStatus === 'alarm'
}

export interface CameraStatus {
	/** Camera position, e.g. "Top right" */
	name: string
	connected: boolean
}

interface CameraHeartbeat {
	topRightCameraConnected?: boolean
	topLeftCameraConnected?: boolean
	bottomLeftCameraConnected?: boolean
	bottomRightCameraConnected?: boolean
	cameraStatuses?: Gen4CameraStatuses
}

interface CameraConnectionStatus {
	connected?: boolean
	disabled?: boolean
	connectedOrDisabled?: boolean
}

interface Gen4CameraStatuses {
	topRight?: CameraConnectionStatus
	topLeft?: CameraConnectionStatus
	bottomLeft?: CameraConnectionStatus
	bottomRight?: CameraConnectionStatus
}

interface VisionCheckStatus {
	heartbeat?: CameraHeartbeat | string
}

interface VisionCheck {
	operational?: boolean
	status?: VisionCheckStatus
}

interface FailedSystemCheckData {
	vision?: VisionCheck
}

interface CameraField {
	field: keyof CameraHeartbeat
	name: string
}

const CAMERA_FIELDS: CameraField[] = [
	{ field: 'topRightCameraConnected', name: 'Top right' },
	{ field: 'topLeftCameraConnected', name: 'Top left' },
	{ field: 'bottomLeftCameraConnected', name: 'Bottom left' },
	{ field: 'bottomRightCameraConnected', name: 'Bottom right' },
]

interface Gen4CameraField {
	field: keyof Gen4CameraStatuses
	name: string
}

const GEN4_CAMERA_FIELDS: Gen4CameraField[] = [
	{ field: 'topRight', name: 'Top right' },
	{ field: 'topLeft', name: 'Top left' },
	{ field: 'bottomLeft', name: 'Bottom left' },
	{ field: 'bottomRight', name: 'Bottom right' },
]

function visionCheck(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): VisionCheck | undefined {
	const data = alert.data ?? {}
	if (alert.type === CABINET_ALERT_TYPE.Vision) return data as VisionCheck
	if (alert.type === CABINET_ALERT_TYPE.FailedSystemCheck) {
		const vision = (data as FailedSystemCheckData).vision
		if (vision?.operational === false) return vision
	}
	return undefined
}

/**
 * Per-camera result of the vision system check behind a camera alert. Empty when
 * every camera was detected (the check failed for another reason) or the camera
 * service didn't answer, as no camera was checked then.
 */
export function cameraStatuses(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): CameraStatus[] {
	const heartbeat = visionCheck(alert)?.status?.heartbeat
	if (!heartbeat || typeof heartbeat !== 'object') return []
	const cameras =
		heartbeat.cameraStatuses && typeof heartbeat.cameraStatuses === 'object'
			? GEN4_CAMERA_FIELDS.flatMap(({ field, name }) => {
					const status = heartbeat.cameraStatuses?.[field]
					if (!status || typeof status.connected !== 'boolean' || status.disabled === true) return []
					return [{ name, connected: status.connectedOrDisabled ?? status.connected }]
				})
			: CAMERA_FIELDS.flatMap(({ field, name }) => {
					const connected = heartbeat[field]
					return typeof connected === 'boolean' ? [{ name, connected }] : []
				})
	return cameras.some((camera) => !camera.connected) ? cameras : []
}

function cameraAlertLabel(alert: CabinetAlertEvent): string {
	const failed = cameraStatuses(alert).filter((camera) => !camera.connected)
	if (failed.length === 1) return `${failed[0].name} camera not working`
	if (failed.length > 1) return `${failed.length} cameras not working`
	return visionLabel(visionCheck(alert))
}

export interface AlertDisplayOptions {
	/** When false, per-camera positions and counts collapse to the generic vision label (QA-330). */
	cameraDiagnostics?: boolean
}

/** Label non-staff users see in place of a staff-only camera warning. */
export const STAFF_ONLY_CAMERA_LABEL = 'Micromart staff only'

/**
 * True when this alert's label embeds granular camera diagnostics — a position name
 * ("Top right camera not working") or a down count ("4 cameras not working"). Composite
 * failed-check labels only ever say "cameras", so they are never granular.
 */
export function hasGranularCameraLabel(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): boolean {
	if (!cameraStatuses(alert).length) return false
	if (alert.type === CABINET_ALERT_TYPE.Vision) return true
	if (alert.type !== CABINET_ALERT_TYPE.FailedSystemCheck) return false
	const failed = labelledFailedParts(alert.data ?? {})
	return failed.length === 1 && failed[0] === CABINET_ALERT_TYPE.Vision
}

/**
 * A granular camera issue that doesn't take the cabinet out of service — hidden from
 * operators, who see a locked "Micromart staff only" indicator instead (QA-330).
 */
export function isCameraStaffOnlyWarning(alert: Pick<CabinetAlertEvent, 'type' | 'data'>): boolean {
	return hasGranularCameraLabel(alert) && !isBlockingAlert(alert)
}

/** Short cause for an out-of-service alert, with tablet detail for connection issues. */
export function alertDetail(alert: CabinetAlertEvent, lastSaleAt?: string | null, opts?: AlertDisplayOptions): string {
	if (opts?.cameraDiagnostics === false && hasGranularCameraLabel(alert)) {
		return isCameraStaffOnlyWarning(alert) ? STAFF_ONLY_CAMERA_LABEL : ALERT_TYPE_LABELS.vision
	}
	if (alert.type === CABINET_ALERT_TYPE.NoSales) return noSalesLabel(alert, lastSaleAt)
	if (alert.type === CABINET_ALERT_TYPE.SalesAnomaly) {
		return alert.data?.trigger === SALES_ANOMALY_TRIGGER.Quiet
			? 'No sales for longer than usual'
			: ALERT_TYPE_LABELS.salesAnomaly
	}
	if (alert.type === CABINET_ALERT_TYPE.OfflineDevice) return `${deviceName(alert) ?? 'Cabinet device'} offline`
	if (alert.type === CABINET_ALERT_TYPE.DeviceNotInKioskMode) {
		return `${deviceName(alert) ?? 'Payment terminal'} not showing the shop app`
	}
	if (alert.type === CABINET_ALERT_TYPE.CabinetOffline && alert.data?.scheduled_restart) {
		if (alert.data.scheduled_restart.grace_ended_at) return "Payment terminal didn't come back after scheduled restart"
		return alert.resolved_at ? 'Payment terminal scheduled restart' : 'Payment terminal restarting (scheduled)'
	}
	if (isTemperatureAlarm(alert)) return TEMPERATURE_ALARM_LABEL
	if (alert.type === CABINET_ALERT_TYPE.MaintenanceMode) {
		const actor = actorDisplayName(alert.triggered_by, alert.triggered_by_username)
		return actor ? `Locked down by ${actor}` : 'Locked down'
	}
	if (alert.type === CABINET_ALERT_TYPE.DoorUnlockFailed) {
		return doorUnlockFailedLabel(alert.data ?? {})
	}
	if (alert.type === CABINET_ALERT_TYPE.UnstableInternet) {
		const { drops24h, drops7d } = (alert.data ?? {}) as { drops24h?: number; drops7d?: number }
		const drops = drops24h && drops24h >= 2 ? `${drops24h} times in 24h` : `${drops7d ?? 0} times in 7 days`
		return `Internet dropped ${drops}`
	}
	if (alert.type === CABINET_ALERT_TYPE.CardReader) return cardReaderLabel(alert.data ?? {})
	if (alert.type === CABINET_ALERT_TYPE.SmartLock) return smartLockLabel(alert.data as SharedSmartLockCheck | null)
	if (alert.type === CABINET_ALERT_TYPE.Vision) return cameraAlertLabel(alert)
	if (alert.type === CABINET_ALERT_TYPE.Display) return displayLabel(alert.data ?? {})
	if (alert.type === CABINET_ALERT_TYPE.FailedSystemCheck) {
		const data = alert.data ?? {}
		const failed = labelledFailedParts(data)
		if (failed.length === 1) {
			if (failed[0] === CABINET_ALERT_TYPE.Vision) return cameraAlertLabel(alert)
			return singleFailedPartLabel(data) ?? ALERT_TYPE_LABELS.failedSystemCheck
		}
		if (failed.length > 1) {
			const names = failed.map((key, index) => {
				const name = SUBSYSTEM_SHORT_NAMES[key] ?? ALERT_TYPE_LABELS[key]?.toLowerCase() ?? key
				return index === 0 ? name : name[0].toLowerCase() + name.slice(1)
			})
			const shown = names.slice(0, 2).join(', ')
			return failed.length > 2
				? `${shown} +${failed.length - 2} more failed checks`
				: `${names[0]} and ${names[1]} failed checks`
		}
	}
	if (alert.type === CABINET_ALERT_TYPE.CabinetOffline && alert.data) {
		const { posOffline, computerOffline } = alert.data as { posOffline?: boolean; computerOffline?: boolean }
		if (posOffline && computerOffline) return 'Computer and payment terminal offline'
		if (posOffline) return 'Payment terminal offline'
		if (computerOffline) return 'Computer offline'
	}
	if (alert.type === CABINET_ALERT_TYPE.LowStock || alert.type === CABINET_ALERT_TYPE.EmptySlots) return stockAlertTitle(alert)
	return alertTypeLabel(alert.type)
}

export function alertStartedAt(alert: Pick<CabinetAlertEvent, 'detected_at' | 'created_at'>): string {
	return alert.detected_at ?? alert.created_at
}

export type AlertRecommendationColor = 'success' | 'default' | 'warning' | 'error'

/** The operator's next step for an issue: a short chip on collapsed rows, the full sentence when expanded. */
export interface AlertRecommendation {
	/** Chip text, e.g. "Check on site" */
	chip: string
	color: AlertRecommendationColor
	/** Full instruction, e.g. "Check the cabinet on site if it persists" */
	label: string
}

interface DeviceAlertData {
	device_name?: string
}

function deviceName(alert: Pick<CabinetAlertEvent, 'data'>): string | undefined {
	const name = (alert.data as DeviceAlertData | null)?.device_name
	return typeof name === 'string' && name.trim() ? name.trim() : undefined
}

export const NO_ACTION: AlertRecommendation = { chip: 'No action needed', color: 'default', label: 'No action needed' }
const SCHEDULED_RESTART_WAIT: AlertRecommendation = {
	chip: 'No action needed',
	color: 'default',
	label: 'No action needed — finishing its scheduled restart.',
}
export const WAIT: AlertRecommendation = {
	chip: 'Wait, usually reconnects',
	color: 'default',
	label: 'Usually reconnects on its own. Check on site if it persists.',
}
export const CHECK_POWER_INTERNET: AlertRecommendation = {
	chip: 'Check power and internet',
	color: 'warning',
	label: "Check the cabinet's power and internet (a power outage is the most common cause).",
}
export const POWER_CYCLE: AlertRecommendation = {
	chip: 'Power cycle cabinet',
	color: 'warning',
	label:
		"Unplug the power cable labeled 'TECHNOLOGY' for 30 seconds and plug it back in. We'll see it reconnect on our end.",
}
export const CHECK_FOOD_SAFETY: AlertRecommendation = {
	chip: 'Check food on site',
	color: 'warning',
	label:
		'Check the food on site and remove any perishable items that may no longer be safe. Then clear the alarm to put the cabinet back in service.',
}

export const CHECK_TEMPERATURE: AlertRecommendation = {
	chip: 'Check food safety',
	color: 'warning',
	label: 'Check the temperature and food safety.',
}
export const CHECK_ON_SITE: AlertRecommendation = {
	chip: 'Check on site',
	color: 'warning',
	label: 'Check the cabinet on site if it persists.',
}
export const COMPARE_TO_USUAL: AlertRecommendation = {
	chip: 'Compare to usual',
	color: 'default',
	label:
		'This can be a slow day, but check the cabinet has stock and that people can still reach it (building open, cabinet not moved or blocked). Visit if it stays quiet.',
}
const SALES_ANOMALY_ACTION: AlertRecommendation = {
	chip: 'Compare to usual',
	color: 'default',
	label:
		'Check that the cabinet has stock and is accessible to customers. If sales remain low, consider visiting the location.',
}
const RESTOCK: AlertRecommendation = {
	chip: 'Restock',
	color: 'warning',
	label: 'Schedule a restock soon to replenish inventory and prevent products from selling out.',
}
function lowStockRecommendation(data: StockAlertData): AlertRecommendation {
	const { empty_slots: emptySlots, total_slots: totalSlots } = data
	if (typeof emptySlots !== 'number' || typeof totalSlots !== 'number' || emptySlots <= 0) return RESTOCK
	const verb = emptySlots === 1 ? 'is' : 'are'
	return { ...RESTOCK, label: `${RESTOCK.label} ${emptySlots} of ${totalSlots} slots ${verb} empty.` }
}
const FILL_EMPTY_SLOTS: AlertRecommendation = {
	chip: 'Restock',
	color: 'warning',
	label: 'Restock the empty slots to ensure products remain available for purchase.',
}
export const IMPROVE_INTERNET: AlertRecommendation = {
	chip: 'Improve internet',
	color: 'warning',
	label: 'Check power and signal at the cabinet, or contact support to switch it to Wi-Fi.',
}
export const VISIT_ON_SITE: AlertRecommendation = {
	chip: 'Visit cabinet',
	color: 'error',
	label: "This can't be fixed remotely. Someone needs to check the cabinet on site.",
}
export const CHECK_TOP_SCREEN: AlertRecommendation = {
	chip: 'Check top screen',
	color: 'warning',
	label: 'Check the top screen is switched on and its HDMI cable is plugged in firmly at both ends.',
}
export const CHECK_TOP_SCREEN_THEN_POWER_CYCLE: AlertRecommendation = {
	chip: 'Power cycle cabinet',
	color: 'warning',
	label:
		"Check the top screen is switched on, then unplug the power cable labeled 'TECHNOLOGY' for 30 seconds and plug it back in.",
}
export const CONTACT_SUPPORT: AlertRecommendation = {
	chip: 'Contact support',
	color: 'error',
	label: 'Contact support',
}
export const OFFLINE_DEVICE_RECOMMENDATION: AlertRecommendation = {
	chip: 'Check device',
	color: 'warning',
	label:
		"One of this cabinet's devices (tablet, computer or payment terminal) stopped checking in. Check it is powered on and connected, then power cycle the cabinet if it stays offline.",
}
export const DEVICE_NOT_IN_KIOSK_MODE_RECOMMENDATION: AlertRecommendation = {
	chip: 'Restart tablet',
	color: 'warning',
	label:
		"The cabinet tablet has left the shop app, so customers can't shop on it. Restart the tablet so it returns to the shop app. Contact support if it doesn't.",
}

/** Per-subsystem alerts the monitor raises on a hard kiosk system-check fault, and the operator's next step for each. */
const SUBSYSTEM_RECOMMENDATIONS: Record<string, AlertRecommendation> = {
	[CABINET_ALERT_TYPE.CardReader]: POWER_CYCLE,
	[CABINET_ALERT_TYPE.Computer]: POWER_CYCLE,
	[CABINET_ALERT_TYPE.Router]: CHECK_POWER_INTERNET,
	[CABINET_ALERT_TYPE.SmartLock]: CHECK_ON_SITE,
	[CABINET_ALERT_TYPE.Vision]: VISIT_ON_SITE,
	[CABINET_ALERT_TYPE.Shelves]: VISIT_ON_SITE,
	[CABINET_ALERT_TYPE.Display]: VISIT_ON_SITE,
	[CABINET_ALERT_TYPE.EtagHub]: VISIT_ON_SITE,
}

/** Alerts younger than this usually clear on their own (most first-trigger offline alerts resolve in minutes). */
const TRANSIENT_OFFLINE_MINUTES = 20
/** The cooler controller refuses unlocks for up to ~6 minutes after power-up (startup delay). */
const CONTROLLER_STARTUP_MINUTES = 10
const ONSITE_OFFLINE_HOURS = 1

const ROOT_CAUSE_RECOMMENDATIONS: Record<string, AlertRecommendation> = {
	router_offline: CHECK_POWER_INTERNET,
	site_unreachable: CHECK_POWER_INTERNET,
	wan_down: CHECK_POWER_INTERNET,
	carrier_issue: CHECK_POWER_INTERNET,
	power_outage: CHECK_POWER_INTERNET,
	computer_offline: POWER_CYCLE,
	pos_off_lan: POWER_CYCLE,
	pos_socket_dead: POWER_CYCLE,
	hardware_failure: VISIT_ON_SITE,
}

export const DOOR_UNLOCK_FAILURE = {
	FoodSafetyLockout: 'food-safety-lockout',
	ControllerNotReady: 'controller-not-ready',
	ControllerNoResponse: 'controller-no-response',
	CamerasUnreachable: 'cameras-unreachable',
	CamerasDisabled: 'cameras-disabled',
	Unknown: 'unknown',
} as const satisfies Record<string, string>
export type DoorUnlockFailure = (typeof DOOR_UNLOCK_FAILURE)[keyof typeof DOOR_UNLOCK_FAILURE]

interface DoorUnlockErrorData {
	error_message?: string | null
	/** Set by super when the refusal correlates with a temperature alarm (in either order). */
	temperature_lockout?: boolean
}

function displayRecommendation(check: unknown): AlertRecommendation {
	switch (displayFault(check)) {
		case DISPLAY_FAULT.ScreenNotDetected:
			return CHECK_TOP_SCREEN
		case DISPLAY_FAULT.AppNotResponding:
			// HDMI unknown (older kiosk releases, unreadable port): the screen itself may still be the cause.
			return (check as DisplayCheck).status?.hdmiConnected === true ? POWER_CYCLE : CHECK_TOP_SCREEN_THEN_POWER_CYCLE
		default:
			return VISIT_ON_SITE
	}
}

/** Why the kiosk refused to unlock, from the error it stored on the `doorUnlockFailed` alert. */
export function doorUnlockFailure(data: Record<string, unknown> | undefined): DoorUnlockFailure {
	const { error_message: message, temperature_lockout: temperatureLockout } = (data ?? {}) as DoorUnlockErrorData
	if (temperatureLockout) return DOOR_UNLOCK_FAILURE.FoodSafetyLockout
	if (!message) return DOOR_UNLOCK_FAILURE.Unknown
	// Raised for both a latched cooler alarm and the startup delay after power-up.
	if (/not in normal state/i.test(message)) return DOOR_UNLOCK_FAILURE.ControllerNotReady
	if (/cameras have been disabled/i.test(message)) return DOOR_UNLOCK_FAILURE.CamerasDisabled
	if (/Rpi|ECONNREFUSED|EHOSTUNREACH|Cameras not connected/i.test(message))
		return DOOR_UNLOCK_FAILURE.CamerasUnreachable
	if (/TransactionTimedOut|Lock status is not 0|is locked|modbus/i.test(message))
		return DOOR_UNLOCK_FAILURE.ControllerNoResponse
	return DOOR_UNLOCK_FAILURE.Unknown
}

const DOOR_UNLOCK_FAILURE_LABELS: Record<DoorUnlockFailure, string> = {
	[DOOR_UNLOCK_FAILURE.FoodSafetyLockout]: 'Door failed to unlock: food safety lockout',
	[DOOR_UNLOCK_FAILURE.ControllerNotReady]: 'Door failed to unlock: fridge controller not ready',
	[DOOR_UNLOCK_FAILURE.ControllerNoResponse]: "Door failed to unlock: fridge controller didn't respond",
	[DOOR_UNLOCK_FAILURE.CamerasUnreachable]: 'Door failed to unlock: cameras not reachable',
	[DOOR_UNLOCK_FAILURE.CamerasDisabled]: 'Door failed to unlock: cameras disabled',
	[DOOR_UNLOCK_FAILURE.Unknown]: ALERT_TYPE_LABELS.doorUnlockFailed,
}

const SUBSYSTEM_SHORT_NAMES: Record<string, string> = {
	[CABINET_ALERT_TYPE.CardReader]: 'Card reader',
	[CABINET_ALERT_TYPE.Computer]: 'Computer',
	[CABINET_ALERT_TYPE.Display]: 'Top display',
	[CABINET_ALERT_TYPE.EtagHub]: 'Price tag hub',
	[CABINET_ALERT_TYPE.KioskError]: 'Cabinet software',
	[CABINET_ALERT_TYPE.PaymentTerminal]: 'Payment terminal',
	[CABINET_ALERT_TYPE.PosApp]: 'Checkout app',
	[CABINET_ALERT_TYPE.Router]: 'Router',
	[CABINET_ALERT_TYPE.Shelves]: 'Shelves',
	[CABINET_ALERT_TYPE.SmartLock]: 'Smart lock',
	[CABINET_ALERT_TYPE.Vision]: 'Cameras',
}

export function doorUnlockFailedLabel(data: Record<string, unknown> | undefined): string {
	return DOOR_UNLOCK_FAILURE_LABELS[doorUnlockFailure(data)]
}

/** A power cycle that hasn't fixed it after a day is a hardware problem. */
function escalateStalePowerCycle(recommendation: AlertRecommendation, ageMinutes: number): AlertRecommendation {
	const powerCycle = recommendation === POWER_CYCLE || recommendation === CHECK_TOP_SCREEN_THEN_POWER_CYCLE
	return powerCycle && ageMinutes >= 24 * 60 ? VISIT_ON_SITE : recommendation
}

/**
 * The single next step for an operator, derived from the alert type, its data,
 * what Micromart Guardian found, and how long the alert has been open.
 */
export function alertRecommendation(alert: CabinetAlertEvent, now = dayjs()): AlertRecommendation {
	if (alert.resolved_at || alert.investigation_status === 'resolved_by_agent') return NO_ACTION
	if (isRestartGrace(alert)) return SCHEDULED_RESTART_WAIT
	const ageMinutes = now.diff(dayjs(alertStartedAt(alert)), 'minute')

	if (alert.type === CABINET_ALERT_TYPE.OfflineDevice) return OFFLINE_DEVICE_RECOMMENDATION
	if (alert.type === CABINET_ALERT_TYPE.DeviceNotInKioskMode) return DEVICE_NOT_IN_KIOSK_MODE_RECOMMENDATION

	if (alert.type === CABINET_ALERT_TYPE.CabinetOffline) {
		if (alert.root_cause && ROOT_CAUSE_RECOMMENDATIONS[alert.root_cause]) {
			return ROOT_CAUSE_RECOMMENDATIONS[alert.root_cause]
		}
		const { computerOffline, posOffline } = (alert.data ?? {}) as { computerOffline?: boolean; posOffline?: boolean }
		// Both devices down at once points at the site (router, power) rather than one device.
		if (computerOffline && posOffline) {
			return ageMinutes < TRANSIENT_OFFLINE_MINUTES ? WAIT : CHECK_POWER_INTERNET
		}
		if (ageMinutes < ONSITE_OFFLINE_HOURS * 60) return WAIT
		return POWER_CYCLE
	}

	if (isTemperatureAlarm(alert)) return CHECK_FOOD_SAFETY
	if (alert.type === CABINET_ALERT_TYPE.FailedSystemCheck) {
		const failed = failedSubsystems(alert.data ?? {})
		if (failed.includes(CABINET_ALERT_TYPE.SmartLock)) return CHECK_ON_SITE
		if (failed.includes(CABINET_ALERT_TYPE.CardReader) || failed.includes(CABINET_ALERT_TYPE.PosApp)) return POWER_CYCLE
		// A QA recording-test failure is fixed by rebooting the computer, not a site visit.
		if (failed.length === 1 && failed[0] === CABINET_ALERT_TYPE.Display) {
			return escalateStalePowerCycle(displayRecommendation(alert.data?.display), ageMinutes)
		}
		const visionQaOnly = visionQaFailed(alert.data?.vision)
		const visitKeys = [CABINET_ALERT_TYPE.Vision, CABINET_ALERT_TYPE.Display, CABINET_ALERT_TYPE.Shelves]
		if (visitKeys.some((key) => failed.includes(key) && !(key === CABINET_ALERT_TYPE.Vision && visionQaOnly))) {
			return VISIT_ON_SITE
		}
		if (visionQaOnly) return POWER_CYCLE
		return CHECK_ON_SITE
	}

	if (alert.type === CABINET_ALERT_TYPE.Vision && visionQaFailed(alert.data)) return POWER_CYCLE
	if (SUBSYSTEM_RECOMMENDATIONS[alert.type]) {
		const recommendation =
			alert.type === CABINET_ALERT_TYPE.Display ? displayRecommendation(alert.data ?? {}) : SUBSYSTEM_RECOMMENDATIONS[alert.type]
		return escalateStalePowerCycle(recommendation, ageMinutes)
	}
	if (alert.type === CABINET_ALERT_TYPE.Temperature) return CHECK_TEMPERATURE
	if (alert.type === CABINET_ALERT_TYPE.DoorUnlockFailed) {
		const { failed_at: failedAt } = (alert.data ?? {}) as { failed_at?: string | null }
		// The alert row is reused across retriggers, so the startup grace runs from the latest failure.
		const sinceFailureMinutes = failedAt ? now.diff(dayjs(failedAt), 'minute') : ageMinutes
		switch (doorUnlockFailure(alert.data ?? {})) {
			case DOOR_UNLOCK_FAILURE.FoodSafetyLockout:
				return CHECK_FOOD_SAFETY
			case DOOR_UNLOCK_FAILURE.ControllerNotReady:
				return sinceFailureMinutes < CONTROLLER_STARTUP_MINUTES ? WAIT : CHECK_TEMPERATURE
			case DOOR_UNLOCK_FAILURE.CamerasUnreachable:
			case DOOR_UNLOCK_FAILURE.ControllerNoResponse:
				return POWER_CYCLE
			case DOOR_UNLOCK_FAILURE.CamerasDisabled:
				return CONTACT_SUPPORT
			default:
				return CHECK_ON_SITE
		}
	}
	if (alert.type === CABINET_ALERT_TYPE.DoorOpen) return CHECK_ON_SITE
	if (alert.type === CABINET_ALERT_TYPE.SalesAnomaly) return SALES_ANOMALY_ACTION
	if (alert.type === CABINET_ALERT_TYPE.LowStock) return lowStockRecommendation((alert.data ?? {}) as StockAlertData)
	if (alert.type === CABINET_ALERT_TYPE.EmptySlots) return FILL_EMPTY_SLOTS
	if (alert.type === CABINET_ALERT_TYPE.NoSales) return COMPARE_TO_USUAL
	if (alert.type === CABINET_ALERT_TYPE.MaintenanceMode) {
		const actor = actorDisplayName(alert.triggered_by, alert.triggered_by_username)
		const byWhom = actor ? ` by ${actor}` : ''
		const period = byWhom.endsWith('.') ? '' : '.'
		return {
			chip: 'No action needed',
			color: 'default',
			label: `This cabinet was manually locked down${byWhom}${period} No action is required until you're ready to put it back in service.`,
		}
	}
	if (alert.type === CABINET_ALERT_TYPE.TowerOffline) return CHECK_POWER_INTERNET
	if (alert.type === CABINET_ALERT_TYPE.UnstableInternet || alert.type === CABINET_ALERT_TYPE.RouterUptimeLow) {
		return IMPROVE_INTERNET
	}
	return CHECK_ON_SITE
}

export function alertRecommendationLabel(alert: CabinetAlertEvent, now = dayjs()): string {
	return alertRecommendation(alert, now).label
}

/** "Tobin Smith" → "Tobin S." */
const abbreviateName = (full: string): string => {
	const parts = full.trim().split(/\s+/).filter(Boolean)
	const first = parts[0] ?? ''
	const lastInitial = parts.length > 1 ? ` ${parts.at(-1)?.charAt(0).toUpperCase()}.` : ''
	return `${first.charAt(0).toUpperCase()}${first.slice(1)}${lastInitial}`
}

/** Service accounts that act on cabinets — render a friendly name, not the raw username. */
const AUTOMATION_ACTOR_NAMES: Record<string, string> = {
	'ops-mcp': 'Guardian',
}

/** "Tobin S." from the actor's display/full name; falls back to the username. */
export function actorDisplayName(actor: AlertActor | null | undefined, username?: string | null): string | null {
	const name = actor?.name?.trim()
	if (name) return abbreviateName(name)
	const fallback = actor?.username ?? username
	if (!fallback) return null
	if (fallback.startsWith('guardian:')) return 'Guardian'
	return AUTOMATION_ACTOR_NAMES[fallback] ?? (fallback.includes(' ') ? abbreviateName(fallback) : fallback)
}

/** Alert types the monitor raises per failed subsystem alongside the composite `failedSystemCheck`. */
const SUBSYSTEM_ALERT_TYPES = new Set(Object.keys(SUBSYSTEM_RECOMMENDATIONS))

/**
 * Active alerts. A per-subsystem alert is folded into an open composite `failedSystemCheck`
 * only when that subsystem is part of the composite; any other subsystem fault (e.g. a
 * non-blocking top display warning) stays visible as its own row. A top display alert is
 * also folded into an open "computer offline" alert, since the display runs on that computer.
 */
export function activeAlerts(row: CabinetServiceStatusRow): CabinetAlertEvent[] {
	const open = (row.alert_events ?? [])
		.filter((alert) => !alert.resolved_at)
		.filter((alert, _, all) => alert.type !== CABINET_ALERT_TYPE.Display || !isComputerOffline(row, all))
	const composite = open.find((alert) => alert.type === CABINET_ALERT_TYPE.FailedSystemCheck)
	if (!composite) return open
	const folded = new Set(failedSubsystems(composite.data ?? {}))
	return open.filter((alert) => !SUBSYSTEM_ALERT_TYPES.has(alert.type) || !folded.has(alert.type))
}

/**
 * An open offline alert keeps the `computerOffline` it was first raised with, so also require the
 * computer to still be offline now (e.g. it recovered while the payment terminal stayed down).
 */
function isComputerOffline(row: CabinetServiceStatusRow, openAlerts: CabinetAlertEvent[]): boolean {
	if (!row.tablet || row.tablet.is_online || hasRecentHeartbeat(row.tablet)) return false
	return openAlerts.some(
		(alert) => alert.type === CABINET_ALERT_TYPE.CabinetOffline && Boolean(alert.data?.computerOffline)
	)
}

export function warningAlerts(row: CabinetServiceStatusRow): CabinetAlertEvent[] {
	return activeAlerts(row).filter((alert) => !isBlockingAlert(alert))
}

export function blockingAlerts(row: CabinetServiceStatusRow): CabinetAlertEvent[] {
	return activeAlerts(row).filter(isBlockingAlert)
}

export function cabinetStatus(row: CabinetServiceStatusRow): CabinetServiceStatus {
	if (row.status === CABINET_SERVICE_STATUS.Inactive || row.is_inactive) return CABINET_SERVICE_STATUS.Inactive
	return row.status === CABINET_SERVICE_STATUS.OutOfService
		? CABINET_SERVICE_STATUS.OutOfService
		: CABINET_SERVICE_STATUS.Operational
}

/** Heartbeat newer than this still counts as the cabinet checking in, even if the socket is down. */
const HEARTBEAT_FRESH_MINUTES = 15

type ConnectionTablet = {
	is_online?: boolean
	last_heartbeat_at?: string | Date | null
} | null

/** The tablet has checked in recently, which is what backs limited remote access. */
export function hasRecentHeartbeat(tablet?: ConnectionTablet): boolean {
	if (!tablet?.last_heartbeat_at) return false
	return dayjs().diff(dayjs(tablet.last_heartbeat_at), 'minute') <= HEARTBEAT_FRESH_MINUTES
}

/**
 * Socket is down on at least one device, but both the computer and payment terminal are still heartbeating.
 * Remote commands may not land; the cabinet is not fully offline.
 */
export function isLimitedRemoteConnection(
	cabinet?: {
		tablet?: ConnectionTablet
		pos_tablet?: ConnectionTablet
	} | null
): boolean {
	const tablet = cabinet?.tablet
	const posTablet = cabinet?.pos_tablet
	if (!tablet || !posTablet) return false
	if (tablet.is_online && posTablet.is_online) return false
	return hasRecentHeartbeat(tablet) && hasRecentHeartbeat(posTablet)
}

/** Monitoring (alerts, uptime, Guardian) only runs for live sites, mirroring Super's `shouldAlertForSite`. */
export function isCabinetMonitored(row?: CabinetServiceStatusRow | null): boolean {
	return Boolean(row?.site?.is_active) && !row?.site?.is_test_site
}

export function cabinetDisplayName(row?: CabinetServiceStatusRow | null): string {
	if (!row) return 'Cabinet'
	return row.name || row.site?.name || `Cabinet ${row.id}`
}

export function cabinetStoreName(row?: CabinetServiceStatusRow | null): string | undefined {
	return row?.site?.store?.name ?? row?.site?.name ?? undefined
}

export function cabinetStorePhoto(row?: CabinetServiceStatusRow | null): string | null | undefined {
	return row?.site?.store?.photo
}

interface CauseBucket {
	key: string
	singular: string
	plural: string
}

/** Short noun used in the overview stat captions, e.g. "2 offline • 1 safety lockout". */
function causeBucket(alert: CabinetAlertEvent): CauseBucket {
	if (
		isTemperatureAlarm(alert) ||
		alert.type === CABINET_ALERT_TYPE.Temperature ||
		doorUnlockFailure(alert.data ?? {}) === DOOR_UNLOCK_FAILURE.FoodSafetyLockout
	) {
		return { key: 'safety-lockout', singular: 'safety lockout', plural: 'safety lockouts' }
	}
	switch (alert.type) {
		case CABINET_ALERT_TYPE.CabinetOffline:
		case CABINET_ALERT_TYPE.TowerOffline:
			return { key: 'offline', singular: 'offline', plural: 'offline' }
		case CABINET_ALERT_TYPE.MaintenanceMode:
			return { key: 'lockdown', singular: 'lockdown', plural: 'lockdowns' }
		case CABINET_ALERT_TYPE.FailedSystemCheck:
			return { key: 'system-check', singular: 'system check', plural: 'system checks' }
		case CABINET_ALERT_TYPE.NoSales:
			return { key: 'no-sales', singular: 'no sales', plural: 'no sales' }
		case CABINET_ALERT_TYPE.SalesAnomaly:
			return { key: 'low-sales', singular: 'low sales', plural: 'low sales' }
		case CABINET_ALERT_TYPE.DoorUnlockFailed:
			return { key: 'unlock', singular: 'unlock failure', plural: 'unlock failures' }
		default: {
			const label = alertTypeLabel(alert.type).toLowerCase()
			return { key: alert.type, singular: label, plural: label }
		}
	}
}

function primaryIssueAlert(
	row: CabinetServiceStatusRow,
	kind: 'out_of_service' | 'warnings'
): CabinetAlertEvent | undefined {
	if (kind === 'out_of_service') {
		return blockingAlerts(row)[0] ?? row.cabinet_service_outage?.alert_events?.[0]
	}
	return warningAlerts(row)[0]
}

/**
 * Caption under an overview stat. Zero is a sentence; otherwise cabinets are grouped by cause.
 * Returns undefined while the issue list is still loading.
 */
export function fleetIssueCaption(
	rows: CabinetServiceStatusRow[],
	kind: 'out_of_service' | 'warnings',
	count: number,
	ready: boolean
): string | undefined {
	if (count <= 0) {
		return kind === 'out_of_service' ? 'None out of service' : 'None with warnings'
	}
	if (!ready) return undefined

	const matching = rows.filter((row) =>
		kind === 'out_of_service'
			? cabinetStatus(row) === CABINET_SERVICE_STATUS.OutOfService
			: cabinetStatus(row) === CABINET_SERVICE_STATUS.Operational && warningAlerts(row).length > 0
	)

	const groups = new Map<string, { bucket: CauseBucket; count: number }>()
	for (const row of matching) {
		const alert = primaryIssueAlert(row, kind)
		const bucket = alert ? causeBucket(alert) : { key: 'other', singular: 'other', plural: 'other' }
		const existing = groups.get(bucket.key)
		if (existing) existing.count += 1
		else groups.set(bucket.key, { bucket, count: 1 })
	}
	if (matching.length < count) {
		const unmatched = count - matching.length
		const existing = groups.get('other')
		if (existing) existing.count += unmatched
		else groups.set('other', { bucket: { key: 'other', singular: 'other', plural: 'other' }, count: unmatched })
	}

	const ranked = [...groups.values()].sort((a, b) => {
		const aOther = a.bucket.key === 'other' ? 1 : 0
		const bOther = b.bucket.key === 'other' ? 1 : 0
		if (aOther !== bOther) return aOther - bOther
		return b.count - a.count || a.bucket.singular.localeCompare(b.bucket.singular)
	})
	const primary = ranked[0]
	if (!primary) return undefined
	const primaryLabel = `${primary.count} ${primary.count === 1 ? primary.bucket.singular : primary.bucket.plural}`
	const restCount = count - primary.count
	if (restCount <= 0) {
		if (kind === 'warnings' && count === 1) return `${primaryLabel} warning`
		return primaryLabel
	}
	return `${primaryLabel} • ${restCount} other`
}

/** "x% operational", or "None monitored yet". */
export function operationalStatCaption(summary: { total: number; operational: number }): string {
	if (summary.total <= 0) return 'None monitored yet'
	const percent = Math.round((summary.operational / summary.total) * 100)
	return `${percent}% operational`
}

export interface CabinetSubstatus {
	/** Highest-severity open alert, plus ` +N` when more alerts are open. */
	label: string
}

/** Open alerts for the status caption, including any on the current outage that aren't already listed. */
function substatusAlerts(row: CabinetServiceStatusRow): CabinetAlertEvent[] {
	const active = activeAlerts(row)
	const seen = new Set(active.map((alert) => alert.id))
	const fromOutage = (row.cabinet_service_outage?.alert_events ?? []).filter(
		(alert) => !alert.resolved_at && !seen.has(alert.id)
	)
	return [...active, ...fromOutage]
}

/** Lower is more severe: food-safety lockout, then anything that takes the cabinet out of service, then warnings. */
function substatusRank(alert: CabinetAlertEvent): number {
	if (isTemperatureAlarm(alert)) return 0
	if (isBlockingAlert(alert)) return 1
	return 2
}

function substatusAlertLabel(alert: CabinetAlertEvent): string {
	if (isTemperatureAlarm(alert)) return TEMPERATURE_ALARM_LABEL
	if (alert.type === CABINET_ALERT_TYPE.SmartLock) return smartLockLabel(alert.data as SharedSmartLockCheck | null)
	return alertTypeLabel(alert.type)
}

/** Caption under the status chip: the most severe open alert, or nothing when there are no alerts. */
export function cabinetSubstatus(row: CabinetServiceStatusRow): CabinetSubstatus | undefined {
	const alerts = substatusAlerts(row)
	if (!alerts.length) return undefined

	const primary = [...alerts].sort((left, right) => substatusRank(left) - substatusRank(right))[0]
	const extra = alerts.length - 1
	const name = substatusAlertLabel(primary)
	return {
		label: extra > 0 ? `${name} +${extra}` : name,
	}
}

/** Second-line figures for a history row, e.g. the sales comparison of a Sales Anomaly warning. */
export function historySubDetail(item: { alert_events?: CabinetAlertEvent[] }): string | null {
	for (const alert of item.alert_events ?? []) {
		const detail = alertSubDetail(alert)
		if (detail) return detail
	}
	return null
}

export function outageCauses(outage: { alert_events?: CabinetAlertEvent[] }, opts?: AlertDisplayOptions): string[] {
	const alerts = outage.alert_events ?? []
	return Array.from(new Set(alerts.map((alert) => alertDetail(alert, undefined, opts))))
}

/**
 * Alerts a remote restart actually addresses, as opposed to a site visit or a self-recovery.
 * A cabinetOffline alert only counts when Guardian's root cause says it restarted something.
 */
const REMOTE_RESTART_TYPES = new Set<string>([
	CABINET_ALERT_TYPE.Computer,
	CABINET_ALERT_TYPE.CardReader,
	CABINET_ALERT_TYPE.PaymentTerminal,
])
const REMOTE_RESTART_CAUSES = new Set(['computer_offline', 'pos_off_lan', 'pos_socket_dead', 'pos_on_homescreen'])

const RECOVERY_BY_TYPE: Record<string, string> = {
	[CABINET_ALERT_TYPE.CabinetOffline]: 'Back online',
	[CABINET_ALERT_TYPE.TowerOffline]: 'Back online',
	[CABINET_ALERT_TYPE.FailedSystemCheck]: 'Checks passing',
	[CABINET_ALERT_TYPE.MaintenanceMode]: 'Lockdown ended',
	[CABINET_ALERT_TYPE.Temperature]: 'Alarm cleared',
	[CABINET_ALERT_TYPE.DoorUnlockFailed]: 'Unlocks succeeded',
	[CABINET_ALERT_TYPE.DoorOpen]: 'Door closed',
	[CABINET_ALERT_TYPE.NoSales]: 'Sales resumed',
	[CABINET_ALERT_TYPE.SalesAnomaly]: 'Sales back to usual',
	[CABINET_ALERT_TYPE.LowStock]: 'Restocked',
	[CABINET_ALERT_TYPE.EmptySlots]: 'Restocked',
	[CABINET_ALERT_TYPE.UnstableInternet]: 'Connection stabilized',
	[CABINET_ALERT_TYPE.RouterUptimeLow]: 'Connection stabilized',
	[CABINET_ALERT_TYPE.PaymentTerminal]: 'Payments restored',
	[CABINET_ALERT_TYPE.CardReader]: 'Card reader restored',
	[CABINET_ALERT_TYPE.SmartLock]: 'Fridge controller restored',
	[CABINET_ALERT_TYPE.Vision]: 'Cameras restored',
	[CABINET_ALERT_TYPE.Shelves]: 'Shelf sensors restored',
	[CABINET_ALERT_TYPE.Router]: 'Router restored',
	[CABINET_ALERT_TYPE.Display]: 'Top display restored',
	[CABINET_ALERT_TYPE.EtagHub]: 'Price tag hub restored',
	[CABINET_ALERT_TYPE.Computer]: 'Computer restored',
	[CABINET_ALERT_TYPE.KioskError]: 'Software recovered',
	[CABINET_ALERT_TYPE.PosApp]: 'Checkout app restored',
	[CABINET_ALERT_TYPE.OfflineDevice]: 'Device back online',
}

function primaryResolvedAlert(alerts: CabinetAlertEvent[]): CabinetAlertEvent | undefined {
	// Prefer an alert that wasn't caused by the reboot itself: in a mixed outage the
	// pos_restart alert is the reboot's symptom, and the label should describe the
	// other fault. When every alert is reboot-caused they stay in the running.
	const others = alerts.filter((alert) => !(alert.resolved_at && alert.data?.pos_restart))
	const list = others.length ? others : alerts
	return list.find(isTemperatureAlarm) ?? list.find(isBlockingAlert) ?? list[0]
}

/** The issue name for a cleared row, matching the alert that `resolutionLabel` describes. */
export function resolvedIssueLabel(
	item: { kind?: 'outage' | 'warning'; alert_events?: CabinetAlertEvent[] },
	opts?: AlertDisplayOptions
): string {
	const alerts = item.alert_events ?? []
	const primary = primaryResolvedAlert(alerts)
	if (!primary) return item.kind === 'warning' ? 'Warning' : 'Service unavailable'
	const extra = new Set(alerts.map((alert) => alertDetail(alert, undefined, opts))).size - 1
	const label = alertDetail(primary, undefined, opts)
	return extra > 0 ? `${label} +${extra}` : label
}

export const HEALTH_HISTORY_WINDOW_DAYS = 14

/** Mirrors Super's POS_RESTART_GRACE_MINUTES; longer outages mean the card reader didn't come back after the reboot. */
export const POS_RESTART_MAX_MINUTES = 30
export const POS_REBOOT_LABEL = 'Payment terminal reboot'

/**
 * The history-row label for a reboot outage: "Scheduled payment terminal reboot" when every
 * stamped alert belongs to a configured reboot window (`pos_restart.scheduled` or a
 * scheduled_restart grace alert), plain "Payment terminal reboot" for a detected
 * unscheduled reboot.
 */
export function posRebootLabel(item: Pick<CabinetServiceHistoryItem, 'alert_events'>): string {
	const scheduled = (item.alert_events ?? []).every(
		(alert) =>
			Boolean((alert.data?.pos_restart as { scheduled?: boolean } | undefined)?.scheduled) ||
			Boolean(alert.data?.scheduled_restart)
	)
	return scheduled ? 'Scheduled payment terminal reboot' : POS_REBOOT_LABEL
}
export const POS_REBOOT_COLOR = 'var(--mui-palette-text-secondary)'
export const POS_REBOOT_ICON_BG = 'var(--mui-palette-action-selected)'

export function isNonPerishableAutoClearAlert(
	alert: Pick<CabinetAlertEvent, 'type' | 'data' | 'cabinet_service_outage_id'>
): boolean {
	return (
		alert.type === CABINET_ALERT_TYPE.FailedSystemCheck &&
		(alert.cabinet_service_outage_id === null || alert.cabinet_service_outage_id === undefined) &&
		alert.data?.[NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY] !== null &&
		alert.data?.[NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY] !== undefined
	)
}

export function nonPerishableAutoClearCountLabel(item: Pick<CabinetServiceHistoryItem, 'alert_events'>): string | null {
	const alert = (item.alert_events ?? []).find(isNonPerishableAutoClearAlert)
	if (!alert) return null
	const marker = alert.data?.[NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY] as { status?: string } | undefined
	if (marker?.status === 'pending') return 'Not confirmed'
	const triggerCount = alert.trigger_count ?? 1
	return triggerCount >= 2 ? `Auto-cleared ×${triggerCount}` : 'Auto-cleared'
}

export function isNonPerishableAutoClearWarning(
	item: Pick<CabinetServiceHistoryItem, 'kind' | 'ended_at' | 'alert_events'>
): boolean {
	return (
		item.kind === 'warning' &&
		item.ended_at !== null &&
		(item.alert_events ?? []).some(
			(alert) => alert.resolved_at !== null && alert.resolved_at !== undefined && isNonPerishableAutoClearAlert(alert)
		)
	)
}

function clearedWithinRebootGrace(item: Pick<CabinetServiceHistoryItem, 'started_at' | 'ended_at'>): boolean {
	if (!item.ended_at) return false
	return dayjs(item.ended_at).diff(dayjs(item.started_at)) <= POS_RESTART_MAX_MINUTES * 60_000
}

/**
 * The independent question a resolved issue answers for an operator:
 * - `service`: could customers buy? (temperature lockouts, card reader, cameras, door unlocks)
 * - `connection`: could we reach the cabinet's devices? (offline, unstable internet, router)
 * - `operations`: does it need route/merchandising work? (stock, slots, sales, price tags)
 */
export type ResolvedIssueAxis = 'service' | 'connection' | 'operations'

export interface ResolvedCabinetIssueBucket {
	label: string
	kind: 'outage' | 'warning'
	axis: ResolvedIssueAxis
	count: number
	totalMinutes: number
}

export interface ResolvedCabinetGroup {
	cabinet_id: number
	cabinet: CabinetServiceHistoryItem['cabinet']
	items: CabinetServiceHistoryItem[]
	/** Most recently resolved outage when the cabinet had one; otherwise the most recently resolved warning. */
	latest: CabinetServiceHistoryItem
	latestOutage: CabinetServiceHistoryItem | null
	issues: ResolvedCabinetIssueBucket[]
	serviceIssues: ResolvedCabinetIssueBucket[]
	connectionIssues: ResolvedCabinetIssueBucket[]
	operationsIssues: ResolvedCabinetIssueBucket[]
	summary: string
	outageCount: number
	warningCount: number
	outageMinutes: number
	warningMinutes: number
	recurring: boolean
}

const SIGNIFICANT_OUTAGE_MIN_MINUTES = 10

/** Alerts about reaching the cabinet's devices, not about whether customers could shop. */
const CONNECTION_ALERT_TYPES = new Set<string>([
	CABINET_ALERT_TYPE.CabinetOffline,
	CABINET_ALERT_TYPE.TowerOffline,
	CABINET_ALERT_TYPE.OfflineDevice,
	CABINET_ALERT_TYPE.UnstableInternet,
	CABINET_ALERT_TYPE.RouterUptimeLow,
	CABINET_ALERT_TYPE.Router,
])

/** Route/merchandising alerts: restocking, sales, and price-tag freshness. */
const OPERATIONS_ALERT_TYPES = new Set<string>([
	CABINET_ALERT_TYPE.SalesAnomaly,
	CABINET_ALERT_TYPE.NoSales,
	CABINET_ALERT_TYPE.LowStock,
	CABINET_ALERT_TYPE.EmptySlots,
	CABINET_ALERT_TYPE.EtagHub,
])

function isScheduledOrRebootAlert(alert: CabinetAlertEvent): boolean {
	return Boolean(alert.data?.scheduled_restart || alert.data?.pos_restart)
}

function itemDurationMinutes(item: Pick<CabinetServiceHistoryItem, 'started_at' | 'ended_at'>): number {
	if (!item.ended_at) return 0
	return Math.max(0, Math.round(dayjs(item.ended_at).diff(dayjs(item.started_at), 'minute', true)))
}

export function resolvedItemAxis(item: CabinetServiceHistoryItem): ResolvedIssueAxis {
	const alerts = (item.alert_events ?? []).filter((alert) => !isScheduledOrRebootAlert(alert))
	const primary = primaryResolvedAlert(alerts.length ? alerts : (item.alert_events ?? []))
	if (primary && CONNECTION_ALERT_TYPES.has(primary.type)) return 'connection'
	if (primary && OPERATIONS_ALERT_TYPES.has(primary.type)) return 'operations'
	return 'service'
}

/**
 * Excludes scheduled/POS reboots, non-perishable auto-clears, and brief offline
 * blips of 10 minutes or less so Recently resolved (By cabinet) doesn't get
 * flooded by routine reboots.
 */
export function isSignificantResolvedItem(item: CabinetServiceHistoryItem): boolean {
	if (isPosRestartOutage(item) || isNonPerishableAutoClearWarning(item)) return false
	const alerts = item.alert_events ?? []
	if (alerts.length > 0 && alerts.every(isScheduledOrRebootAlert)) return false
	const hasOfflineOnly =
		alerts.length > 0 &&
		alerts.every(
			(alert) =>
				alert.type === CABINET_ALERT_TYPE.CabinetOffline || alert.type === CABINET_ALERT_TYPE.OfflineDevice
		)
	if (hasOfflineOnly && itemDurationMinutes(item) <= SIGNIFICANT_OUTAGE_MIN_MINUTES) {
		return false
	}
	return true
}

function groupedResolvedIssueLabel(item: CabinetServiceHistoryItem, opts?: AlertDisplayOptions): string {
	const alerts = (item.alert_events ?? []).filter((alert) => !isScheduledOrRebootAlert(alert))
	const primary = primaryResolvedAlert(alerts.length ? alerts : (item.alert_events ?? []))
	if (primary?.type === CABINET_ALERT_TYPE.CabinetOffline || primary?.type === CABINET_ALERT_TYPE.OfflineDevice) {
		return 'Offline'
	}
	if (primary?.type === CABINET_ALERT_TYPE.DoorUnlockFailed) {
		return 'Door failed to unlock'
	}
	const raw = resolvedIssueLabel(item, opts)
	return raw.replace(/\s+\+\d+$/, '').replace(/\s*\(\d+\s+times\)$/i, '')
}

/**
 * Groups resolved history items by cabinet into independent Service (could customers
 * buy?), Connection (could we reach the devices?), and Operations (does it need a route
 * visit?) tracks, collapsing related offline/unlock labels and prioritizing the last
 * resolved out-of-service outage on `latest`.
 */
export function groupResolvedByCabinet(
	items: CabinetServiceHistoryItem[],
	opts?: AlertDisplayOptions
): ResolvedCabinetGroup[] {
	const byCabinet = new Map<number, CabinetServiceHistoryItem[]>()
	for (const item of items) {
		if (item.cabinet_id === null || !isSignificantResolvedItem(item)) continue
		const existing = byCabinet.get(item.cabinet_id)
		if (existing) existing.push(item)
		else byCabinet.set(item.cabinet_id, [item])
	}
	const groups: ResolvedCabinetGroup[] = []
	for (const [cabinetId, cabinetItems] of byCabinet.entries()) {
		const buckets = new Map<string, ResolvedCabinetIssueBucket>()
		let outageCount = 0
		let warningCount = 0
		let outageMinutes = 0
		let warningMinutes = 0
		let latestOutage: CabinetServiceHistoryItem | null = null

		for (const item of cabinetItems) {
			const label = groupedResolvedIssueLabel(item, opts)
			const minutes = itemDurationMinutes(item)
			const isOutage = item.kind === 'outage'
			const axis = resolvedItemAxis(item)
			const bucketKey = `${axis}:${isOutage ? 'outage' : 'warning'}:${label}`
			if (isOutage) {
				outageCount += 1
				outageMinutes += minutes
				if (!latestOutage) latestOutage = item
			} else {
				warningCount += 1
				warningMinutes += minutes
			}
			const existing = buckets.get(bucketKey)
			if (existing) {
				existing.count += 1
				existing.totalMinutes += minutes
			} else {
				buckets.set(bucketKey, {
					label,
					kind: isOutage ? 'outage' : 'warning',
					axis,
					count: 1,
					totalMinutes: minutes,
				})
			}
		}

		const issues = [...buckets.values()].sort((a, b) => {
			if (a.kind !== b.kind) return a.kind === 'outage' ? -1 : 1
			return b.count - a.count
		})
		const byAxis = (axis: ResolvedIssueAxis) => issues.filter((bucket) => bucket.axis === axis)
		const serviceIssues = byAxis('service')
		const connectionIssues = byAxis('connection')
		const operationsIssues = byAxis('operations')
		const summary = issues
			.map((bucket) => (bucket.count > 1 ? `${bucket.label} (×${bucket.count})` : bucket.label))
			.join(' • ')

		groups.push({
			cabinet_id: cabinetId,
			cabinet: cabinetItems[0].cabinet,
			items: cabinetItems,
			latest: latestOutage ?? cabinetItems[0],
			latestOutage,
			issues,
			serviceIssues,
			connectionIssues,
			operationsIssues,
			summary,
			outageCount,
			warningCount,
			outageMinutes,
			warningMinutes,
			recurring: issues.some((bucket) => bucket.count >= 2),
		})
	}
	return groups.sort((left, right) => {
		const leftHasOutage = left.outageCount > 0
		const rightHasOutage = right.outageCount > 0
		if (leftHasOutage !== rightHasOutage) return leftHasOutage ? -1 : 1
		if (leftHasOutage && rightHasOutage) {
			const leftRecurringOutage = left.issues.some((b) => b.kind === 'outage' && b.count >= 2)
			const rightRecurringOutage = right.issues.some((b) => b.kind === 'outage' && b.count >= 2)
			if (leftRecurringOutage !== rightRecurringOutage) return leftRecurringOutage ? -1 : 1
		}
		if (left.recurring !== right.recurring) return left.recurring ? -1 : 1
		return (right.latest.ended_at ?? '').localeCompare(left.latest.ended_at ?? '')
	})
}

// `pos_restart` alone is enough — the monitor only stamps it when the device-reported
// boot time matches the fault timing, so the scheduled-window inference isn't needed
// to call it a reboot.
function isPosRestartAlert(alert: CabinetAlertEvent): boolean {
	if (alert.data?.scheduled_restart?.grace_ended_at) return false
	if (alert.data?.pos_restart) return !!alert.resolved_at
	return isRestartGrace(alert)
}

/** A resolved issue caused only by a detected POS restart, cleared within POS_RESTART_MAX_MINUTES. */
export function isPosRestartOutage(
	item: Pick<CabinetServiceHistoryItem, 'started_at' | 'ended_at' | 'alert_events'>
): boolean {
	if (!item.ended_at) return false
	const alerts = item.alert_events ?? []
	if (!alerts.length || !alerts.every(isPosRestartAlert)) return false
	return clearedWithinRebootGrace(item)
}

/** Guardian's plain-language result for an issue, from the first alert that has one. */
export function guardianSummary(alerts: Pick<CabinetAlertEvent, 'operator_summary'>[] | undefined): string | null {
	const summary = alerts?.find((alert) => alert.operator_summary?.trim())?.operator_summary
	return summary?.trim() || null
}

/** Who cleared the cooler alarm, from the stamp `clearCoolerAlarm` writes on the alert. */
function alarmClearedBy(alert: Pick<CabinetAlertEvent, 'data'>): string | null {
	const username = alert.data?.alarm_cleared_by_username
	if (!username) return null
	if (username.startsWith('guardian:') || username === 'ops-mcp') return 'Guardian'
	return alert.data?.alarm_cleared_by_name ?? username
}

/** Short outcome for a cleared issue, e.g. "Alarm cleared by Guardian" or "Restarted remotely". */
export function resolutionLabel(
	item: Pick<CabinetServiceHistoryItem, 'started_at' | 'ended_at' | 'alert_events'> & {
		restored_by_username?: string | null
		restored_by_name?: string | null
	}
): string {
	const alerts = item.alert_events ?? []
	const primary = primaryResolvedAlert(alerts)
	if (primary && isNonPerishableAutoClearAlert(primary)) {
		const marker = primary.data?.[NON_PERISHABLE_AUTO_CLEAR_MARKER_KEY] as { status?: string } | undefined
		return marker?.status === 'pending' ? NON_PERISHABLE_AUTO_CLEAR_PENDING_LABEL : NON_PERISHABLE_AUTO_CLEAR_LABEL
	}
	if (primary && (isTemperatureAlarm(primary) || primary.type === CABINET_ALERT_TYPE.Temperature)) {
		const clearedBy = alarmClearedBy(primary)
		return clearedBy ? `Alarm cleared by ${clearedBy}` : 'Alarm cleared'
	}
	if (primary?.type === CABINET_ALERT_TYPE.DoorUnlockFailed) return 'Unlocks succeeded'
	if (primary?.type === CABINET_ALERT_TYPE.NoSales) return 'Sales resumed'
	if (primary?.type === CABINET_ALERT_TYPE.SalesAnomaly) return 'Sales back to usual'

	const restartedRemotely = alerts.some(
		(alert) =>
			alert.investigation_status === 'resolved_by_agent' &&
			(REMOTE_RESTART_TYPES.has(alert.type) || REMOTE_RESTART_CAUSES.has(alert.root_cause ?? ''))
	)
	if (restartedRemotely) return 'Restarted remotely'

	// Only when the whole issue was the restart (isPosRestartOutage also enforces the
	// grace window): a mixed outage — reboot plus an unrelated fault — falls through
	// to the recovery label for what actually failed.
	const hasPosRestart = alerts.some((alert) => alert.resolved_at && alert.data?.pos_restart)
	if (hasPosRestart && isPosRestartOutage(item)) return 'Restarted'

	if (primary?.type === CABINET_ALERT_TYPE.FailedSystemCheck) {
		const failed = failedSubsystems(primary.data ?? {})
		if (failed.length === 1 && RECOVERY_BY_TYPE[failed[0]]) return RECOVERY_BY_TYPE[failed[0]]
	}
	if (primary?.type === CABINET_ALERT_TYPE.DeviceNotInKioskMode) {
		return `${deviceName(primary) ?? 'Card reader'} back on the shop app`
	}
	if (primary && RECOVERY_BY_TYPE[primary.type]) return RECOVERY_BY_TYPE[primary.type]
	if (item.restored_by_username) {
		return `Cleared by ${item.restored_by_name ?? actorDisplayName({ username: item.restored_by_username })}`
	}
	return 'Recovered'
}

export const MIN_UPTIME_WINDOW_MINUTES = 24 * 60

export function formatCabinetUptime(
	downtimeMinutes: number | null | undefined,
	uptimeWindowMinutes: number | null | undefined
): string {
	if (
		uptimeWindowMinutes !== null &&
		uptimeWindowMinutes !== undefined &&
		uptimeWindowMinutes < MIN_UPTIME_WINDOW_MINUTES
	) {
		return '—'
	}
	if (downtimeMinutes === null || downtimeMinutes === undefined) return '—'
	const windowMinutes = Math.max(1, uptimeWindowMinutes ?? 30 * 24 * 60)
	const uptime = Math.max(0, 100 - (downtimeMinutes / windowMinutes) * 100)
	return uptime >= 99.95 ? '100%' : `${uptime.toFixed(1)}%`
}

export function outageDuration(outage: { started_at: string; ended_at?: string | null }): string {
	const end = outage.ended_at ? dayjs(outage.ended_at) : dayjs()
	return formatAlertAge(end.diff(dayjs(outage.started_at), 'minute'))
}
